import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download-icon.svg';
import { ReactComponent as OpenIcon } from '../../../../assets/icons/unfold-icon.svg';
import Popup from '../../../Popup/Popup';
import { exportElementToPdf } from '../../../../services/exportService';
import './Table.css';
import {
    formatValue,
    isNumericType,
    isRightAligned,
    formatDisplay,
    getCellClass,
    computeTotalsRow,
    computeGroupTotals as computeGroupTotalsUtil,
} from './tableUtils';

function Table({ component }) {
    const { header = "Table", subheader = "Data Table", columns = [], columnData = [], footer = null } = component;

    // Determine grouping settings (support legacy groupings array of strings or objects)
    const grouping = useMemo(() => {
        const g = component && typeof component === 'object' ? component.grouping : null;
        if (g && typeof g === 'object') return g;
        const gs = component && typeof component === 'object' ? component.groupings : null;
        // If first element is an object, assume it's already a grouping object
        if (Array.isArray(gs) && gs.length > 0) {
            if (gs[0] && typeof gs[0] === 'object' && !Array.isArray(gs[0])) return gs[0];
            // If array of strings, convert to a simple grouping by the first string selector
            if (typeof gs[0] === 'string') {
                return { group_by: gs[0], show_subtotal: true, default_collapsed: false };
            }
        }
        return null;
    }, [component]);
    // Disable grouping when all groups are singletons (no parent has >1 child)
    const groupingEff = useMemo(() => {
        if (!(grouping && grouping.group_by)) return null;
        try {
            const groupBy = grouping.group_by;
            const rawRows = Array.isArray(component?.data) ? component.data : null;
            const baseColumns = Array.isArray(columns) ? columns : [];
            const baseData = Array.isArray(columnData) ? columnData : [];

            // Prefer using rawRows when aligned with columnData
            if (rawRows && rawRows.length === baseData.length) {
                const counts = new Map();
                for (let i = 0; i < rawRows.length; i++) {
                    const key = rawRows[i] && Object.prototype.hasOwnProperty.call(rawRows[i], groupBy) ? rawRows[i][groupBy] : '';
                    const k = String(key);
                    counts.set(k, (counts.get(k) || 0) + 1);
                }
                const hasMulti = Array.from(counts.values()).some((c) => c > 1);
                return hasMulti ? grouping : null;
            }

            // Fallback: if the grouping selector exists as a base column, compute from baseData
            const idx = baseColumns.findIndex(c => c && c.selector === groupBy);
            if (idx >= 0) {
                const counts = new Map();
                for (let i = 0; i < baseData.length; i++) {
                    const row = baseData[i] || [];
                    const key = row[idx];
                    const k = String(key);
                    counts.set(k, (counts.get(k) || 0) + 1);
                }
                const hasMulti = Array.from(counts.values()).some((c) => c > 1);
                return hasMulti ? grouping : null;
            }
        } catch (_) {}
        return grouping; // default to provided grouping when uncertain
    }, [grouping, component, columns, columnData]);
    // Support multi-level grouping: prefer `component.groupings` array of strings
    // Additionally, disable grouping entirely when ALL groups would be singletons (no parent has >1 child)
    const groupSelectors = useMemo(() => {
        // Build initial selectors list from explicit groupings array or fallback to single grouping
        const baseSelectors = (() => {
            const gs = component && typeof component === 'object' ? component.groupings : null;
            if (Array.isArray(gs) && gs.length > 0) {
                return gs.filter((s) => typeof s === 'string');
            }
            const gb = groupingEff?.group_by;
            if (gb && typeof gb === 'string') return [gb];
            return [];
        })();

        if (baseSelectors.length === 0) return [];

        // Determine if the primary selector would create any group with more than one child
        try {
            const primary = baseSelectors[0];
            const rawRows = Array.isArray(component?.data) ? component.data : null;
            const baseColumns = Array.isArray(columns) ? columns : [];
            const baseData = Array.isArray(columnData) ? columnData : [];

            if (rawRows && rawRows.length === baseData.length) {
                const counts = new Map();
                for (let i = 0; i < rawRows.length; i++) {
                    const key = rawRows[i] && Object.prototype.hasOwnProperty.call(rawRows[i], primary) ? rawRows[i][primary] : '';
                    const k = String(key);
                    counts.set(k, (counts.get(k) || 0) + 1);
                }
                const hasMulti = Array.from(counts.values()).some((c) => c > 1);
                return hasMulti ? baseSelectors : [];
            }

            const idx = baseColumns.findIndex(c => c && c.selector === primary);
            if (idx >= 0) {
                const counts = new Map();
                for (let i = 0; i < baseData.length; i++) {
                    const row = baseData[i] || [];
                    const key = row[idx];
                    const k = String(key);
                    counts.set(k, (counts.get(k) || 0) + 1);
                }
                const hasMulti = Array.from(counts.values()).some((c) => c > 1);
                return hasMulti ? baseSelectors : [];
            }
        } catch (_) {}

        // If uncertain, keep requested grouping
        return baseSelectors;
    }, [component, groupingEff, columns, columnData]);
    const hasGrouping = groupSelectors.length > 0;
    const primaryGroupSelector = groupSelectors[0] || null;
    const secondaryGroupSelector = groupSelectors[1] || null;
    const hasTwoLevels = groupSelectors.length >= 2;
    const showGroupSubtotal = hasGrouping ? (groupingEff?.show_subtotal !== false) : false;

    // Normalize columns/rows: if grouping by a selector not present as a column,
    // auto-inject it as the first column using raw row objects from component.data
    const { columnsN, columnDataN } = useMemo(() => {
        const baseColumns = Array.isArray(columns) ? columns : [];
        const baseData = Array.isArray(columnData) ? columnData : [];
        let nextColumns = baseColumns;
        let nextData = baseData;

        if (hasGrouping && primaryGroupSelector && baseColumns.findIndex(c => c?.selector === primaryGroupSelector) < 0) {
            const rawRows = Array.isArray(component?.data) ? component.data : null;
            if (rawRows && rawRows.length === baseData.length) {
                const prettyName = String(primaryGroupSelector)
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/(^|\s)\S/g, (t) => t.toUpperCase());
                const injectedCol = { name: prettyName, selector: primaryGroupSelector, type: 'string' };
                nextColumns = [injectedCol, ...baseColumns];
                nextData = baseData.map((row, idx) => [
                    (rawRows[idx] && Object.prototype.hasOwnProperty.call(rawRows[idx], primaryGroupSelector)) ? rawRows[idx][primaryGroupSelector] : '',
                    ...row
                ]);
            }
        }

        return { columnsN: nextColumns, columnDataN: nextData };
    }, [columns, columnData, hasGrouping, primaryGroupSelector, component]);

    // Totals handling (new-only)
    const totalObj = component && typeof component === 'object' ? component.total : null;
    const showColumnTotals = !!(totalObj && totalObj.show_total);
    const showRowTotals = false; // new spec: no right/row totals unless added later
    const totalColumnsSet = new Set(Array.isArray(totalObj?.columns_to_include) ? totalObj.columns_to_include : []);


    // Compute per-row totals if enabled
    const computeRowTotal = (row) => {
        if (!showRowTotals) return null;
        let sum = 0;
        columnsN.forEach((col, idx) => {
            const eligible = totalColumnsSet.size === 0 || totalColumnsSet.has(col.selector);
            if (eligible && ['currency', 'number', 'percent', 'percentage'].includes(col.type)) {
                const v = Number(row[idx]);
                if (!isNaN(v)) sum += v;
            }
        });
        return sum;
    };

    // Compute column totals if enabled
    const totalsRow = showColumnTotals ? computeTotalsRow(columnsN, columnDataN, totalColumnsSet) : null;

    // Grand total (bottom-right) when both row and column totals are enabled
    const grandTotal = showRowTotals && showColumnTotals
        ? columnDataN.reduce((acc, row) => acc + (computeRowTotal(row) || 0), 0)
        : null;

    // Grouping calculations (support one or two levels)
    const groupIdx1 = hasGrouping ? columnsN.findIndex(c => c?.selector === primaryGroupSelector) : -1;
    const groupIdx2 = hasTwoLevels ? columnsN.findIndex(c => c?.selector === secondaryGroupSelector) : -1;

    const groups = useMemo(() => {
        if (!hasGrouping || groupIdx1 < 0) return [];
        // One-level grouping
        if (!hasTwoLevels || groupIdx2 < 0) {
            const order = [];
            const map = new Map();
            for (let i = 0; i < columnDataN.length; i++) {
                const row = columnDataN[i];
                const key = row[groupIdx1];
                const k = String(key);
                if (!map.has(k)) { map.set(k, []); order.push(k); }
                map.get(k).push(row);
            }
            return order.map(k => ({ key: k, rows: map.get(k) }));
        }

        // Two-level grouping
        const order1 = [];
        const map1 = new Map(); // key1 -> { rows: [...], order2: [], map2 }
        for (let i = 0; i < columnDataN.length; i++) {
            const row = columnDataN[i];
            const k1 = String(row[groupIdx1]);
            const k2 = String(row[groupIdx2]);
            if (!map1.has(k1)) {
                map1.set(k1, { rows: [], order2: [], map2: new Map() });
                order1.push(k1);
            }
            const bucket = map1.get(k1);
            bucket.rows.push(row);
            if (!bucket.map2.has(k2)) {
                bucket.map2.set(k2, []);
                bucket.order2.push(k2);
            }
            bucket.map2.get(k2).push(row);
        }
        return order1.map((k1) => {
            const bucket = map1.get(k1);
            const children = bucket.order2.map((k2) => ({ key: k2, rows: bucket.map2.get(k2) }));
            return { key: k1, rows: bucket.rows, children };
        });
    }, [hasGrouping, hasTwoLevels, groupIdx1, groupIdx2, columnDataN]);

    // Determine if any second-level group has more than one row
    const secondLevelHasMulti = useMemo(() => {
        if (!hasTwoLevels) return false;
        for (let i = 0; i < groups.length; i++) {
            const g = groups[i];
            if (Array.isArray(g.children)) {
                for (let j = 0; j < g.children.length; j++) {
                    const c = g.children[j];
                    if (Array.isArray(c.rows) && c.rows.length > 1) return true;
                }
            }
        }
        return false;
    }, [hasTwoLevels, groups]);

    // Expanded state for one or two levels
    const [expandedGroupsL1, setExpandedGroupsL1] = useState(() => {
        if (!hasGrouping) return new Set();
        const set = new Set();
        if (!groupingEff?.default_collapsed) {
            groups.forEach(g => set.add(g.key));
        }
        return set;
    });

    const [expandedGroupsL2, setExpandedGroupsL2] = useState(() => {
        if (!hasTwoLevels) return new Set();
        const set = new Set();
        if (!groupingEff?.default_collapsed) {
            groups.forEach(g => {
                if (Array.isArray(g.children)) {
                    g.children.forEach((c) => set.add(`${g.key}||${c.key}`));
                }
            });
        }
        return set;
    });

    const toggleGroupL1 = (key) => {
        setExpandedGroupsL1(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleGroupL2 = (parentKey, key) => {
        const id = `${parentKey}||${key}`;
        setExpandedGroupsL2(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const computeGroupTotals = (rows) => computeGroupTotalsUtil(rows, columnsN, totalColumnsSet, groupingEff?.calcs);

    // Removed unused buildGroupSubtotalLabel helper

    // Column resizing state and handlers
    const headerCount = columnsN.length + (showRowTotals ? 1 : 0);
    const [colWidths, setColWidths] = useState(() => Array(headerCount).fill(null));
    const tableRefInline = useRef(null);
    const containerRefInline = useRef(null);
    const tableRefPopup = useRef(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const dragRef = useRef(null);

    useEffect(() => {
        if (colWidths.length !== headerCount) {
            setColWidths(prev => {
                const next = Array(headerCount).fill(null);
                for (let i = 0; i < Math.min(prev.length, headerCount); i++) next[i] = prev[i];
                return next;
            });
        }
    }, [headerCount]);

    const getMeasuredWidth = (index) => {
        try {
            const th = tableRefInline.current?.querySelector('thead tr')?.children?.[index];
            if (th) return Math.max(60, Math.round(th.getBoundingClientRect().width));
        } catch (_) {}
        return 140;
    };

    const stopDragging = () => {
        window.removeEventListener('mousemove', onDrag);
        window.removeEventListener('mouseup', stopDragging);
        dragRef.current = null;
    };

    const onDrag = (e) => {
        const ctx = dragRef.current;
        if (!ctx) return;
        const delta = e.clientX - ctx.startX;
        const newWidth = Math.max(60, ctx.startWidth + delta);
        setColWidths(prev => {
            const next = [...prev];
            next[ctx.index] = newWidth;
            return next;
        });
    };

    const onResizeStart = (index, e) => {
        e.preventDefault();
        const startWidth = colWidths[index] ?? getMeasuredWidth(index);
        dragRef.current = { index, startX: e.clientX, startWidth };
        window.addEventListener('mousemove', onDrag);
        window.addEventListener('mouseup', stopDragging);
    };

    const handleDownload = async () => {
        try {
            const target = containerRefInline.current;
            if (!target) return;
            const safeHeader = String(header || 'table').replace(/[^a-z0-9\-\s_]/gi, '').replace(/\s+/g, '_').toLowerCase();
            // Expand scroll areas and hide action icons for a clean export
            target.classList.add('export-mode');
            await exportElementToPdf(target, {
                filename: `${safeHeader}.pdf`,
                pageOrientation: 'landscape',
                margin: 10,
                scale: 2,
            });
            target.classList.remove('export-mode');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to export table to PDF', err);
        } finally {
            const target = containerRefInline.current;
            if (target) target.classList.remove('export-mode');
        }
    };

    const HeaderSection = () => (
        (header || subheader) ? (
            <div className="header header-with-actions">
                <div className="header-text-group">
                    <div className="header-text">{header}</div>
                    <div className="subheader-text">{subheader}</div>
                </div>
                <div className="header-actions">
                    <DownloadIcon className="icon" onClick={handleDownload}/>
                    <OpenIcon className="icon enlarge" onClick={() => setIsPopupOpen(true)}/>
                </div>
            </div>
        ) : null
    );

    const TableMarkup = ({ tableRefProp }) => (
        <div className="table-scroll-wrapper">
            <table className="data-table" ref={tableRefProp}>
                <colgroup>
                    {Array.from({ length: headerCount }).map((_, i) => (
                        <col key={i} style={colWidths[i] ? { width: colWidths[i] + 'px' } : undefined} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        {columnsN.map((col, index) => {
                            const isRight = isRightAligned(col.type);
                            return (
                                <th key={index} scope="col" className="table-header-cell" data-align={isRight ? 'right' : undefined}>
                                    <span className="th-label">{col.name}</span>
                                    {index < headerCount - 1 && (
                                        <span className="col-resizer" role="separator" aria-orientation="vertical" onMouseDown={(e) => onResizeStart(index, e)} />
                                    )}
                                </th>
                            );
                        })}
                        {showRowTotals && (
                            <th data-align="right" className="table-header-cell total-col-border">
                                <span className="th-label">Total</span>
                                {/* no resizer on the last column */}
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {!hasGrouping && (
                        <>
                            {columnDataN.map((row, rowIdx) => (
                                <tr key={rowIdx}>
                                    {columnsN.map((col, colIdx) => {
                                        const isRight = isRightAligned(col.type);
                                        const value = row[colIdx];
                                        const displayValue = formatDisplay(value, col.type);
                                        return (
                                            <td
                                                key={colIdx}
                                                className={getCellClass(col.type, value)}
                                                data-align={isRight ? "right" : undefined}
                                            >
                                                {displayValue}
                                            </td>
                                        );
                                    })}
                                    {showRowTotals && (
                                        <td className="row-total-cell total-cell cell-number total-col-border" data-align="right">{formatDisplay(computeRowTotal(row), 'number')}</td>
                                    )}
                                </tr>
                            ))}
                        </>
                    )}

                    {hasGrouping && !hasTwoLevels && groups.map((g) => {
                        const isExpanded = expandedGroupsL1.has(g.key);
                        const groupTotals = showGroupSubtotal ? computeGroupTotals(g.rows) : null;
                        const groupRowTotal = showGroupSubtotal && showRowTotals
                            ? g.rows.reduce((acc, r) => acc + (computeRowTotal(r) || 0), 0)
                            : null;
                        return (
                            <React.Fragment key={g.key}>
                                <tr className="group-header subtotal-row" onClick={() => toggleGroupL1(g.key)}>
                                    {columnsN.map((col, idx) => {
                                        const isRight = isRightAligned(col.type);
                                        if (idx === 0) {
                                            return (
                                                <td key={idx} colSpan={1}>
                                                    <span className="group-header-label" style={{ fontSize: 13 }}>
                                                        {g.key}
                                                        <span className={`group-arrow ${isExpanded ? 'expanded' : 'collapsed'}`}>{isExpanded ? '▾' : '▸'}</span>
                                                    </span>
                                                </td>
                                            );
                                        }
                                        const value = Array.isArray(groupTotals) ? groupTotals[idx] : '';
                                        return (
                                            <td key={idx} className={getCellClass(col.type, value, { subtotal: true })} data-align={isRight ? 'right' : undefined}>
                                                {Array.isArray(groupTotals) ? formatDisplay(value, col.type) : ''}
                                            </td>
                                        );
                                    })}
                                    {showRowTotals && (
                                        <td className="row-total-cell subtotal-cell cell-number total-col-border" data-align="right">{groupRowTotal != null ? formatDisplay(groupRowTotal, 'number') : ''}</td>
                                    )}
                                </tr>
                                {isExpanded && g.rows.map((row, rowIdx) => (
                                    <tr key={g.key + '-' + rowIdx} className="child-row">
                                        {columnsN.map((col, colIdx) => {
                                            const isRight = isRightAligned(col.type);
                                            const value = row[colIdx];
                                            const displayValue = formatDisplay(value, col.type);
                                            return (
                                                <td
                                                    key={colIdx}
                                                    className={getCellClass(col.type, value)}
                                                    data-align={isRight ? "right" : undefined}
                                                    style={colIdx === 0 ? { paddingLeft: 20 } : undefined}
                                                >
                                                    {displayValue}
                                                </td>
                                            );
                                        })}
                                        {showRowTotals && (
                                            <td className="row-total-cell total-cell cell-number total-col-border" data-align="right">{formatDisplay(computeRowTotal(row), 'number')}</td>
                                        )}
                                    </tr>
                                ))}
                            </React.Fragment>
                        );
                    })}

                    {hasGrouping && hasTwoLevels && groups.map((g1) => {
                        const isExpanded1 = expandedGroupsL1.has(g1.key);
                        const groupTotals1 = showGroupSubtotal ? computeGroupTotals(g1.rows) : null;
                        const groupRowTotal1 = showGroupSubtotal && showRowTotals
                            ? g1.rows.reduce((acc, r) => acc + (computeRowTotal(r) || 0), 0)
                            : null;
                        return (
                            <React.Fragment key={g1.key}>
                                <tr className="group-header subtotal-row" onClick={() => toggleGroupL1(g1.key)}>
                                    {columnsN.map((col, idx) => {
                                        const isRight = isRightAligned(col.type);
                                        if (idx === 0) {
                                            return (
                                                <td key={idx} colSpan={1}>
                                                    <span className="group-header-label" style={{ fontSize: 13 }}>
                                                        {g1.key}
                                                        <span className={`group-arrow ${isExpanded1 ? 'expanded' : 'collapsed'}`}>{isExpanded1 ? '▾' : '▸'}</span>
                                                    </span>
                                                </td>
                                            );
                                        }
                                        const value = Array.isArray(groupTotals1) ? groupTotals1[idx] : '';
                                        return (
                                            <td key={idx} className={getCellClass(col.type, value, { subtotal: true })} data-align={isRight ? 'right' : undefined}>
                                                {Array.isArray(groupTotals1) ? formatDisplay(value, col.type) : ''}
                                            </td>
                                        );
                                    })}
                                    {showRowTotals && (
                                        <td className="row-total-cell subtotal-cell cell-number total-col-border" data-align="right">{groupRowTotal1 != null ? formatDisplay(groupRowTotal1, 'number') : ''}</td>
                                    )}
                                </tr>

                                {isExpanded1 && Array.isArray(g1.children) && (
                                    secondLevelHasMulti
                                    ? g1.children.map((g2) => {
                                    const id2 = `${g1.key}||${g2.key}`;
                                    const isExpanded2 = expandedGroupsL2.has(id2);
                                    const groupTotals2 = showGroupSubtotal ? computeGroupTotals(g2.rows) : null;
                                    const groupRowTotal2 = showGroupSubtotal && showRowTotals
                                        ? g2.rows.reduce((acc, r) => acc + (computeRowTotal(r) || 0), 0)
                                        : null;
                                        return (
                                        <React.Fragment key={id2}>
                                            <tr className="group-header subtotal-row" onClick={() => toggleGroupL2(g1.key, g2.key)}>
                                                {columnsN.map((col, idx) => {
                                                    const isRight = isRightAligned(col.type);
                                                    if (idx === 0) {
                                                        return (
                                                            <td key={idx} colSpan={1}>
                                                                <span className="group-header-label" style={{ fontSize: 13, paddingLeft: 20 }}>
                                                                    {g2.key}
                                                                    <span className={`group-arrow ${isExpanded2 ? 'expanded' : 'collapsed'}`}>{isExpanded2 ? '▾' : '▸'}</span>
                                                                </span>
                                                            </td>
                                                        );
                                                    }
                                                    const value = Array.isArray(groupTotals2) ? groupTotals2[idx] : '';
                                                    return (
                                                        <td key={idx} className={getCellClass(col.type, value, { subtotal: true })} data-align={isRight ? 'right' : undefined}>
                                                            {Array.isArray(groupTotals2) ? formatDisplay(value, col.type) : ''}
                                                        </td>
                                                    );
                                                })}
                                                {showRowTotals && (
                                                    <td className="row-total-cell subtotal-cell cell-number total-col-border" data-align="right">{groupRowTotal2 != null ? formatDisplay(groupRowTotal2, 'number') : ''}</td>
                                                )}
                                            </tr>

                                            {isExpanded2 && g2.rows.map((row, rowIdx) => (
                                                <tr key={id2 + '-' + rowIdx} className="child-row">
                                                    {columnsN.map((col, colIdx) => {
                                                        const isRight = isRightAligned(col.type);
                                                        const value = row[colIdx];
                                                        const displayValue = formatDisplay(value, col.type);
                                                        return (
                                                            <td
                                                                key={colIdx}
                                                                className={getCellClass(col.type, value)}
                                                                data-align={isRight ? "right" : undefined}
                                                                style={colIdx === 0 ? { paddingLeft: 40 } : undefined}
                                                            >
                                                                {displayValue}
                                                            </td>
                                                        );
                                                    })}
                                                    {showRowTotals && (
                                                        <td className="row-total-cell total-cell cell-number total-col-border" data-align="right">{formatDisplay(computeRowTotal(row), 'number')}</td>
                                                    )}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                        );
                                    })
                                    : g1.children.flatMap((g2, g2Idx) => (
                                        g2.rows.map((row, rowIdx) => (
                                            <tr key={`${g1.key}||${g2.key}-${rowIdx}`} className="child-row">
                                                {columnsN.map((col, colIdx) => {
                                                    const isRight = isRightAligned(col.type);
                                                    const value = row[colIdx];
                                                    const displayValue = formatDisplay(value, col.type);
                                                    return (
                                                        <td
                                                            key={colIdx}
                                                            className={getCellClass(col.type, value)}
                                                            data-align={isRight ? "right" : undefined}
                                                            style={colIdx === 0 ? { paddingLeft: 20 } : undefined}
                                                        >
                                                            {displayValue}
                                                        </td>
                                                    );
                                                })}
                                                {showRowTotals && (
                                                    <td className="row-total-cell total-cell cell-number total-col-border" data-align="right">{formatDisplay(computeRowTotal(row), 'number')}</td>
                                                )}
                                            </tr>
                                        ))
                                    ))
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
                {(showColumnTotals || (Array.isArray(footer) && footer.length > 0)) && (
                    <tfoot>
                        {showColumnTotals && (
                            <tr className="table-total">
                                {totalsRow.map((value, idx) => {
                                    const type = columnsN[idx]?.type;
                                    const isRight = isRightAligned(type);
                                    return (
                                        <td key={`tot-${idx}`} className={getCellClass(type, value, { total: true })} data-align={isRight ? 'right' : undefined}>
                                            {formatDisplay(value, type)}
                                        </td>
                                    );
                                })}
                                {showRowTotals && (
                                    <td className="grand-total-cell total-cell cell-number total-col-border" data-align="right">{formatDisplay(grandTotal, 'number')}</td>
                                )}
                            </tr>
                        )}
                        {Array.isArray(footer) && footer.length > 0 && footer.map((rowObj, fIdx) => {
                            const values = columnsN.map(col => (rowObj && Object.prototype.hasOwnProperty.call(rowObj, col.selector)) ? rowObj[col.selector] : '');
                            return (
                                <tr key={`foot-${fIdx}`} className="table-total">
                                    {values.map((value, idx) => {
                                        const type = columnsN[idx]?.type;
                                        const isRight = isRightAligned(type);
                                        return (
                                            <td key={`foot-${fIdx}-${idx}`} className={getCellClass(type, value, { total: true })} data-align={isRight ? 'right' : undefined}>
                                                {formatDisplay(value, type)}
                                            </td>
                                        );
                                    })}
                                    {showRowTotals && (
                                        <td className="total-cell total-col-border" />
                                    )}
                                </tr>
                            );
                        })}
                    </tfoot>
                )}
            </table>
        </div>
    );

    return (
        <div className="report-component table" ref={containerRefInline}>
            <HeaderSection />
            <div className="content-container">
                <div className="component-content">
                    <TableMarkup tableRefProp={tableRefInline} />
                </div>
            </div>
            {isPopupOpen && (
                <Popup
                    onClose={() => setIsPopupOpen(false)}
                    content={(
                        <div className="table-popup-content" onClick={(e) => e.stopPropagation()}>
                            <div className="report-component table">
                                <div className="header header-with-actions">
                                    <div className="header-text-group">
                                        <div className="header-text">{header}</div>
                                        <div className="subheader-text">{subheader}</div>
                                    </div>
                                    <div className="header-actions">
                                        <span className="icon" role="button" aria-label="Close" title="Close" onClick={() => setIsPopupOpen(false)}>✕</span>
                                    </div>
                                </div>
                                <div className="content-container">
                                    <div className="component-content">
                                        <TableMarkup tableRefProp={tableRefPopup} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                />
            )}
        </div>
    );
}

export default Table; 